import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EstimateMappingService } from './estimate-mapping.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-estimate-mapping',
    templateUrl: './estimate-mapping.component.html',
    styleUrls: ['./estimate-mapping.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EstimateMappingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = { api: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  showEntityLoader = true;
  minimise = false;
  saveloader = false;
  isEdit = 0;

  estMapping: any = {
    SCHDM_TYPE_ID: '',
    LINE_DETAILS: '',
    ADJ_TYPE: '',
    ATTRIB_ACCT_REF_KEY: '',
    SCHDM_TYPE_KEY: ''
  };
  estMapping_backup: any = null;
  SchDmTypeList: any[] = [];
  lineDetailList: any[] = [];
  adjTypeList: any[] = [];

  validationFlags = {
    SCHDM_TYPE_ID_NOT_VALID: false,
    LINE_DETAILS_NOT_VALID: false,
    ADJ_TYPE_NOT_VALID: false
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private estimateMappingService: EstimateMappingService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const newRowData = rowPayloadFromActiveScreen(activeScreen);
    if (newRowData && Object.keys(newRowData).length > 0) {
      this.rowData = newRowData;
      this.estMapping_backup = this.rowData;
      this.isEdit = 1;
    }
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) return;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.showEntityLoader = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    const taxYear = this.filterParams?.tax_year ?? this.globalService?.tax_year ?? this.rowData?.TAX_YEAR;
    const jcdKey = this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 493;

    this.estimateMappingService.loadJsonObject(baseUrl, {
      action_code: 'cw1y1d',
      tax_year: taxYear,
      jcd_key: jcdKey
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data.jsonObject) {
          this.SchDmTypeList = data.jsonObject;
          if (this.estMapping_backup) {
            const temp = this.SchDmTypeList.filter((x: any) => x.SCHDM_TEMPLATE_ID === this.estMapping_backup.SCHDM_TEMPLATE_KEY);
            if (temp.length > 0) {
              this.estMapping.SCHDM_TYPE_ID = temp[0].SCHDMID;
              this.estMapping.SCHDM_TYPE_KEY = temp[0].SCHDM_TEMPLATE_ID;
            }
          }
          this.loadAdjType(baseUrl, taxYear, jcdKey);
        }
      },
      () => {
        this.showEntityLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadAdjType(baseUrl: string, taxYear: any, jcdKey: any): void {
    this.estimateMappingService.loadJsonObject(baseUrl, {
      action_code: 'lj8qn3',
      tax_year: taxYear,
      jcd_key: jcdKey
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data.jsonObject) {
          this.adjTypeList = data.jsonObject;
          if (this.estMapping_backup) {
            const temp = this.adjTypeList.filter((x: any) => x.CODE_NAME === this.estMapping_backup.ADJ_TYPE);
            if (temp.length > 0) this.estMapping.ADJ_TYPE = temp[0].CODE_NAME;
          }
          this.loadLineDetails(baseUrl, taxYear, jcdKey);
        } else {
          this.showEntityLoader = false;
          this.cdr.detectChanges();
        }
      },
      () => {
        this.showEntityLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadLineDetails(baseUrl: string, taxYear: any, jcdKey: any): void {
    this.estimateMappingService.loadJsonObject(baseUrl, {
      action_code: 'xlfyyh',
      tax_year: taxYear,
      jcd_key: jcdKey
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data.jsonObject) {
          this.lineDetailList = data.jsonObject;
          if (this.estMapping_backup) {
            const temp = this.lineDetailList.filter((x: any) => x.ATTRIB_ACCT_REFERENCE_KEY === this.estMapping_backup.ATTRIB_ACCT_REFERENCE_KEY);
            if (temp.length > 0) {
              this.estMapping.LINE_DETAILS = temp[0].LINE_DETAIL;
              this.estMapping.ATTRIB_ACCT_REF_KEY = temp[0].ATTRIB_ACCT_REFERENCE_KEY;
            }
          }
        }
        this.showEntityLoader = false;
        this.cdr.detectChanges();
      },
      () => {
        this.showEntityLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  onLineDetailsChange(): void {
    const temp = this.lineDetailList.filter((x: any) => x.LINE_DETAIL === this.estMapping.LINE_DETAILS);
    this.estMapping.ATTRIB_ACCT_REF_KEY = temp.length > 0 ? temp[0].ATTRIB_ACCT_REFERENCE_KEY : null;
    this.cdr.detectChanges();
  }

  isValid(): boolean {
    this.validationFlags.SCHDM_TYPE_ID_NOT_VALID = !this.estMapping.SCHDM_TYPE_ID;
    this.validationFlags.LINE_DETAILS_NOT_VALID = !this.estMapping.LINE_DETAILS;
    this.validationFlags.ADJ_TYPE_NOT_VALID = !this.estMapping.ADJ_TYPE;
    this.cdr.detectChanges();
    return !this.validationFlags.SCHDM_TYPE_ID_NOT_VALID &&
           !this.validationFlags.LINE_DETAILS_NOT_VALID &&
           !this.validationFlags.ADJ_TYPE_NOT_VALID;
  }

  estimateMapping(isDeleteFlag: boolean): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--yellow');
      return;
    }
    if (this.saveloader) return;
    this.saveloader = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    const payload = {
      ...this.estMapping,
      isDelete: isDeleteFlag,
      isEdit: this.isEdit
    };
    this.estimateMappingService.saveMapping(
      baseUrl,
      payload,
      isDeleteFlag,
      !!this.isEdit,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.saveloader = false;
        this.cdr.detectChanges();
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(
            isDeleteFlag ? 'Estimated Mapping deleted successfully' : 'Estimated Mapping saved successfully',
            'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
        }
      },
      () => {
        this.saveloader = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error saving estimate mapping', 'gtw-snackbar--red');
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
